import {
  ShippingMethodOptionProps,
  ShippingMethodOption as BaseShippingMethodOption,
} from "@made-people/blueprint-checkout";
import { useCentraSelectionResponse } from "@made-people/grease-centra";
import { useTranslate } from "@made-people/grease-translations";
import { useCartState } from "@made-people/grease-cart";
import { Checkmark } from "../../../assets/icons/Checkmark";
import {
  getCarrierLogo,
  isGenericCarrierWarehouse,
} from "../../../utils/get-carrier-logo";
import { getCartDeliveryPriority } from "../../../utils/get-cart-delivery-priority";
import { useAllocationRule } from "../../../services/centra/allocation-rule/mm-allocation-rule-provider";
import { useShippingThreshold } from "../../../hooks/use-shipping-threshold";
import { useCartWarehouseStock } from "../../../hooks/use-cart-warehouse-stock";
import {
  useDeliveryTimeSettings,
  getDeliveryTimeLabelFromSettings,
} from "../../../hooks/use-delivery-time-settings";
import { useDeliveryDataStatus } from "../../../hooks/use-delivery-data-status";

export const MMShippingMethodOption = (props: ShippingMethodOptionProps) => {
  const selectionResponse = useCentraSelectionResponse();
  const { isFreeShipping } = useShippingThreshold();
  const translate = useTranslate();
  const { cart } = useCartState();
  const { allocationRule } = useAllocationRule();
  const { stockByVariant } = useCartWarehouseStock();
  const deliveryTimeSettings = useDeliveryTimeSettings();
  const deliveryDataStatus = useDeliveryDataStatus();

  const warehouses = allocationRule?.warehouses ?? [];
  const deliveryPriority = getCartDeliveryPriority(
    cart?.items ?? [],
    warehouses,
    stockByVariant,
  );

  const logo = getCarrierLogo(
    selectionResponse?.selection?.address?.country || "",
    props.methodKey,
    props.label,
    warehouses[0]?.name,
  );

  // MMCentraShippingMethodsRenderer holds the whole list back until the
  // allocation rule and warehouse stock have settled, so by the time a row
  // renders the priority is either known or known to be unavailable. On error
  // the Storyblok label is skipped entirely — an unknown priority resolves to
  // "mixed", which would state a delivery time the order may not have.
  const deliveryTimeLabel =
    deliveryDataStatus === "ready"
      ? getDeliveryTimeLabelFromSettings(deliveryTimeSettings, deliveryPriority)
      : undefined;

  const displayLabel =
    props.methodKey === "EXPRESS"
      ? props.label
      : (deliveryTimeLabel ?? props.label);

  // Warehouses with no known carrier show no logo at all, matching every other
  // country without a mapping. Express then needs the badge to stay
  // distinguishable from standard delivery.
  const description =
    props.methodKey === "EXPRESS" &&
    isGenericCarrierWarehouse(warehouses[0]?.name) ? (
      <span className="w-fit rounded bg-light-blue-10 px-2 py-1 text-body-xs">
        {translate("Checkout_Shipping_Express")}
      </span>
    ) : undefined;

  const displayPrice =
    isFreeShipping && props.methodKey !== "EXPRESS"
      ? translate("Checkout_CartTotals_FreeShipping")
      : props.price;

  return (
    <BaseShippingMethodOption
      logo={logo}
      icons={{ active: <Checkmark className="w-5 h-5" /> }}
      {...props}
      label={displayLabel}
      description={description}
      price={displayPrice}
    />
  );
};
